(() => {
  const BUNDLE_SOURCE_SELECTOR = '[data-amelya-cart-bundle-progress]';
  const CHECKOUT_URL = '/discount/AMELYA20?redirect=/checkout';

  const BEST_SELLER_STORAGE_KEY = 'amelya_best_seller_bundle_selected';

  const GIFT_KEYS = [
    '_Amelya promotional gift',
    '_Gift linked to bundle',
    '_Gift discount required'
  ];

  let isProcessing = false;
  let checkoutRedirectInProgress = false;

  function sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function safeCheckoutRedirect() {
    if (checkoutRedirectInProgress) return;

    checkoutRedirectInProgress = true;

    window.setTimeout(() => {
      window.location.href = CHECKOUT_URL;
    }, 200);
  }

  function bundleSource() {
    return document.querySelector(BUNDLE_SOURCE_SELECTOR);
  }

  function getBundleVariantId(size) {
    const source = bundleSource();
    if (!source) return 0;

    return Number(
      source.dataset[`bundle${size}VariantId`] ||
      source.getAttribute(`data-bundle-${size}-variant-id`) ||
      source.getAttribute(`data-bundle${size}-variant-id`) ||
      0
    );
  }

  function cameFromBestSellerBundle() {
    return sessionStorage.getItem(BEST_SELLER_STORAGE_KEY) === 'true';
  }

  function clearBestSellerBundleFlag() {
    sessionStorage.removeItem(BEST_SELLER_STORAGE_KEY);
  }

  function isGiftLine(item) {
    const props = item.properties || {};
    return GIFT_KEYS.some((key) => Boolean(props[key]));
  }

  function bundleSizeFromLine(item) {
    const props = item.properties || {};

    if (props.Bundle) {
      const match = String(props.Bundle).match(/(2|3|4)/);
      if (match) return Number(match[1]);
    }

    const variantId = Number(item.variant_id);

    if (variantId === getBundleVariantId(2)) return 2;
    if (variantId === getBundleVariantId(3)) return 3;
    if (variantId === getBundleVariantId(4)) return 4;

    return 0;
  }

  function isBundleLine(item) {
    return bundleSizeFromLine(item) > 0;
  }

  function categoryFromItem(item) {
    return item.product_type || item.category || '';
  }

  function eligibleUnits(cart) {
    const units = [];

    (cart.items || []).forEach((item) => {
      if (isGiftLine(item)) return;
      if (isBundleLine(item)) return;

      const qty = parseInt(item.quantity || 0, 10);

      for (let i = 0; i < qty; i += 1) {
        units.push({
          item,
          key: item.key
        });
      }
    });

    return units;
  }

  function activeBundleSize(cart) {
    let size = 0;

    (cart.items || []).forEach((item) => {
      size = Math.max(size, bundleSizeFromLine(item));
    });

    return size;
  }

  function buildProperties(size, units) {
    const properties = {
      Bundle: `${size} pieces`
    };

    units.slice(0, size).forEach(({ item }, index) => {
      const n = index + 1;

      properties[`Piece ${n}`] = [
        categoryFromItem(item),
        item.product_title || item.title || ''
      ].filter(Boolean).join(' · ');

      properties[`_Piece ${n} variant`] =
        item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title
          : '';

      properties[`_Piece ${n} variant ID`] = String(item.variant_id || '');
      properties[`_Piece ${n} SKU`] = item.sku || '';
      properties[`_Piece ${n} image`] = item.image || '';
      properties[`_Piece ${n} category`] = categoryFromItem(item);
    });

    return properties;
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Could not read cart.');
    }

    return response.json();
  }

  async function postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Cart update failed.');
    }

    return data;
  }

  async function convertFourPiecesToBundle(cart) {
    const existingBundleSize = activeBundleSize(cart);

    if (existingBundleSize >= 4) {
      return true;
    }

    const units = eligibleUnits(cart);

    if (units.length < 4) {
      return false;
    }

    const bundleVariantId = getBundleVariantId(4);

    if (!bundleVariantId) {
      console.warn('[AMELYA GIFT AUTO BUNDLE] Bundle 4 variant ID not found.');
      return false;
    }

    const selectedUnits = units.slice(0, 4);
    const properties = buildProperties(4, selectedUnits);
    const usedByKey = {};

    selectedUnits.forEach((unit) => {
      usedByKey[unit.key] = (usedByKey[unit.key] || 0) + 1;
    });

    for (const [key, quantityToRemove] of Object.entries(usedByKey)) {
      const originalItem = (cart.items || []).find((item) => item.key === key);
      if (!originalItem) continue;

      const newQuantity = Math.max(Number(originalItem.quantity || 0) - quantityToRemove, 0);

      await postCart(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        id: key,
        quantity: newQuantity
      });
    }

    await postCart(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      items: [
        {
          id: Number(bundleVariantId),
          quantity: 1,
          properties
        }
      ]
    });

    return true;
  }

  async function maybeAutoBundleAndCheckout() {
    if (isProcessing || checkoutRedirectInProgress) return;

    isProcessing = true;

    try {
      const cart = await fetchCart();

      const existingBundleSize = activeBundleSize(cart);
      const units = eligibleUnits(cart);
      const fromBestSellerBundle = cameFromBestSellerBundle();

      const hasFiveEligibleUnits = units.length >= 5;
      const hasBundleFourAndExtraProduct = existingBundleSize >= 4 && units.length >= 1;

      const shouldAutoCheckout =
        hasFiveEligibleUnits ||
        hasBundleFourAndExtraProduct ||
        (fromBestSellerBundle && units.length >= 5);

      if (!shouldAutoCheckout) {
        isProcessing = false;
        return;
      }

      const converted = await convertFourPiecesToBundle(cart);

      if (!converted) {
        isProcessing = false;
        return;
      }

      clearBestSellerBundleFlag();

      await sleep(200);
      safeCheckoutRedirect();
    } catch (error) {
      console.error('[AMELYA GIFT AUTO BUNDLE]', error);
      isProcessing = false;
    }
  }

  function isCheckoutIntent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest('a[href*="/checkout"]') ||
      target.closest('button[name="checkout"]') ||
      target.closest('input[name="checkout"]') ||
      target.closest('[data-cart-checkout]') ||
      target.closest('[data-checkout-button]') ||
      target.closest('.cart__checkout-button') ||
      target.closest('.cart-drawer__checkout') ||
      target.closest('#checkout')
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!isCheckoutIntent(event)) return;

      window.setTimeout(maybeAutoBundleAndCheckout, 120);
    },
    true
  );

  window.AmelyaGiftAutoBundle = {
    maybeAutoBundleAndCheckout
  };
})();